import { cn } from "@/lib/utils";
import type { JobWithRelations } from "@/types/db";
import { trackApplyClick } from "@/hooks/useAnalytics";

interface JobApplyButtonProps {
  job: JobWithRelations;
  variant: "summary" | "detail_desktop" | "detail_bottom" | "mobile_sticky";
}

const ELEMENT_KEY: Record<JobApplyButtonProps["variant"], string> = {
  summary: "job.jobCardApplySummary",
  detail_desktop: "job.jobDetailApplyDesktop",
  detail_bottom: "job.jobDetailContentEnd",
  mobile_sticky: "job.mobileFooterBtn",
};

const VARIANT_CLASSES: Record<JobApplyButtonProps["variant"], string> = {
  summary: "hidden jobApplyBtn p-4 group-hover:flex",
  detail_desktop: "jobApplyBtn px-8 py-3",
  detail_bottom: "w-full p-4",
  mobile_sticky: "p-4 w-full",
};

// Accept only http(s) schemes — block javascript:, data:, vbscript:, etc.
function getSafeApplyUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export default function JobApplyButton({ job, variant }: JobApplyButtonProps) {
  const baseClasses =
    "inline-flex flex-col items-center justify-center font-semibold text-white text-lg rounded-lg transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-300";

  const enabledColor =
    "bg-polaris-orange hover:bg-orange-700 focus-visible:outline-orange-400";
  const disabledColor = "bg-gray-300 text-gray-500 cursor-not-allowed";

  const safeApplyUrl = getSafeApplyUrl(job.applyUrl);
  const hasUrl = safeApplyUrl !== null;
  const label = hasUrl ? (
    <span className="inline-flex items-center gap-1.5">
      Apply now
      <span aria-hidden="true">→</span>
    </span>
  ) : (
    <span>No application link</span>
  );

  const restrictionContent = job.restriction ? (
    <span className="restriction mt-1 text-sm font-semibold">
      {job.restriction}
    </span>
  ) : null;

  if (!hasUrl) {
    return (
      <button
        type="button"
        disabled
        className={cn(baseClasses, VARIANT_CLASSES[variant], disabledColor)}
        data-element={ELEMENT_KEY[variant]}
      >
        {label}
        {restrictionContent}
      </button>
    );
  }

  return (
    <a
      href={safeApplyUrl}
      className={cn(baseClasses, VARIANT_CLASSES[variant], enabledColor)}
      target="_blank"
      rel="noopener noreferrer nofollow"
      onClick={(e) => {
        e.stopPropagation();
        if (job.slug) trackApplyClick(job.slug);
      }}
      data-element={ELEMENT_KEY[variant]}
    >
      {label}
      {restrictionContent}
    </a>
  );
}
